import React, {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import toast from "react-hot-toast";

import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import DashboardCard from "../../components/dashboard/DashboardCard.jsx";
import DashboardGrid from "../../components/dashboard/DashboardGrid.jsx";
import FormControl from "../../components/form/FormControl.jsx";
import FormButton from "../../components/form/FormButton.jsx";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import ProjectProfileLogo from "./ProjectProfileLogo.jsx";
import DeleteProjectContent from "./DeleteProjectContent.jsx";

import {ProjectApi} from "../../api/authority/generated/index.ts";
import useDownloadState from "../../api/utilities/useDownloadState.js";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import BillingDataForm from "./Billing/BillingDataForm.jsx";
import CardForm from "./Billing/CardForm.jsx";
import Button from "../../components/Button.jsx";

export default function ProjectSettings({project}) {
    const {t} = useTranslation();
    const {et} = useErrorTranslation();
    const {getAuthorityConfiguration, resetCurrentUserProjects} = useContext(GlobalContext);

    const initialProjectInputState = {
        name: project.name,
        logoUrl: project.logoUrl,
    };

    const [projectInput, setProjectInput] = useState(initialProjectInputState);
    const [newUri, setNewUri] = useState('');
    const [saveState, setDownloading, setResult, setError] = useDownloadState();

    useEffect(() => {
        setProjectInput(initialProjectInputState);
    }, [project.id]);

    const handleSaveProject = async () => {
        setDownloading(true);
        try {
            const response = await new ProjectApi(getAuthorityConfiguration()).updateProject({
                projectId: project.id,
                projectInput: projectInput,
            });
            setResult(response);
            toast.success(t('saved'));
            resetCurrentUserProjects();
        } catch (e) {
            const errorMessage = await et(e);
            toast.error(errorMessage);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <WideSecureLayout title={t('route.project-settings')}>
            <DashboardGrid>
                <div className={'col-span-full xl:col-span-6 grid sm:gap-6 gap-3'}>
                    <DashboardCard title={t('project.misc-settings')}>
                        <LoadingComponent loading={saveState.downloading}>
                            <ProjectProfileLogo project={project}/>

                            <FormControl
                                className={'mt-3'}
                                key={'name' + project.id}
                                type={'text'}
                                label={t('project.name')}
                                value={projectInput.name}
                                onChange={(e) => setProjectInput({...projectInput, name: e.target.value})}
                            />
                            <div className={'flex justify-end mt-4'}>
                                <Button size="normal" onClick={handleSaveProject}>
                                    {t('save')}
                                </Button>
                            </div>
                        </LoadingComponent>
                    </DashboardCard>

                    <DashboardCard title={t('project.payment-data')}>
                        <CardForm project={project}/>
                    </DashboardCard>

                    <DashboardCard title={t('project.delete')} className={'hidden xl:block'}>
                        <DeleteProjectContent project={project}/>
                    </DashboardCard>
                </div>

                <DashboardCard title={t('project.billing-data')}>
                    <BillingDataForm project={project}/>
                </DashboardCard>

                <DashboardCard title={t('project.delete')} className={'xl:hidden'}>
                    <DeleteProjectContent project={project}/>
                </DashboardCard>
            </DashboardGrid>
        </WideSecureLayout>
    );
}
